import { useState, useEffect, useCallback, useRef } from 'react';
import { usePlatforms } from '../../../hooks/usePlatforms';

const useCompanies = () => {
  const { getCompanies, createCompany } = usePlatforms();
  const [companies, setCompanies] = useState([]);
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const abortControllerRef = useRef(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedField, setSelectedField] = useState('name');

  // Función para mapear datos de la API al formato esperado
  const mapCompanyData = (company) => {
    return {
      id: company.boxitCompanyCode || company.companyCode || company.id || '',
      // Mapear campos según los nombres solicitados
      NombreEmpresa: company.companyName || '',
      CodUser: company.companyCode || '',
      PrimerNombre: company.contactPrimaryFirstName || '',
      PrimerApellido: company.contactPrimaryLastName || '',
      Email: company.contactPrimaryEmail || '',
      ImgLogo: company.logoBase64 || '',
      LockerConsolidation: company.lockerConsolidation || false,
      Activo: company.isActive !== undefined ? company.isActive : true,
      // Mantener datos originales para referencia
      ...company
    };
  };

  // Cargar datos reales de la API con paginación, búsqueda y filtros del servidor
  useEffect(() => {
    const loadCompanies = async () => {
      setLoading(true);
      setError(null);
      
      // Cancelar petición anterior si existe
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      
      // Crear nuevo AbortController
      const controller = new AbortController();
      abortControllerRef.current = controller;
      
      try {
        // Construir objeto de filtros para el backend
        // Ahora soportamos múltiples filtros simultáneos
        const filtersForBackend = {};
        
        // Agregar todos los filtros de búsqueda que estén presentes
        if (filters.name && filters.name.trim()) {
          filtersForBackend.name = filters.name.trim();
        }
        if (filters.companyCode && filters.companyCode.trim()) {
          filtersForBackend.companyCode = filters.companyCode.trim();
        }
        if (filters.email && filters.email.trim()) {
          filtersForBackend.email = filters.email.trim();
        }
        if (filters.contactName && filters.contactName.trim()) {
          filtersForBackend.contactName = filters.contactName.trim();
        }
        
        // Agregar filtro de status
        if (filters.status) {
          filtersForBackend.status = filters.status === 'Active' || filters.status === 'active' 
            ? 'Active' 
            : filters.status === 'Inactive' || filters.status === 'inactive' 
              ? 'Inactive' 
              : filters.status;
        }
        
        // Llamar al backend con filtros
        const result = await getCompanies(
          abortControllerRef.current.signal,
          currentPage,
          itemsPerPage,
          filtersForBackend
        );
        
        // Manejar diferentes formatos de respuesta
        let companiesList = [];
        let total = 0;
        
        if (Array.isArray(result)) {
          companiesList = result;
          total = result.length;
        } else if (result?.payload) {
          companiesList = Array.isArray(result.payload) ? result.payload : [];
          total = result.companiesQty || result.total || companiesList.length;
        } else if (result?.data) {
          companiesList = Array.isArray(result.data) ? result.data : [];
          total = companiesList.length;
        } else if (result?.companies) {
          companiesList = Array.isArray(result.companies) ? result.companies : [];
          total = companiesList.length;
        }
        
        // Mapear los datos al formato esperado
        const mappedCompanies = companiesList.map(mapCompanyData);
        
        // Aplicar ordenamiento client-side (si es necesario)
        let sortedCompanies = mappedCompanies;
        if (sortConfig.key) {
          sortedCompanies = [...mappedCompanies].sort((a, b) => {
            let aValue = a[sortConfig.key];
            let bValue = b[sortConfig.key];

            // Para valores booleanos
            if (sortConfig.key === 'LockerConsolidation' || sortConfig.key === 'Activo') {
              aValue = aValue ? 1 : 0;
              bValue = bValue ? 1 : 0;
            }

            // Manejar valores nulos/undefined
            if (aValue == null && bValue == null) return 0;
            if (aValue == null) return 1;
            if (bValue == null) return -1;

            // Comparación de strings
            if (typeof aValue === 'string' && typeof bValue === 'string') {
              aValue = aValue.toLowerCase();
              bValue = bValue.toLowerCase();
            }

            if (aValue < bValue) {
              return sortConfig.direction === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
              return sortConfig.direction === 'asc' ? 1 : -1;
            }
            return 0;
          });
        }
        
        setCompanies(sortedCompanies);
        setFilteredCompanies(sortedCompanies);
        setTotalItems(total);
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Error loading companies:', err);
          setError(err.message || 'Error al cargar las empresas');
          setCompanies([]);
          setFilteredCompanies([]);
          setTotalItems(0);
        }
      } finally {
        // Only the request that was not superseded may clear the flag. An
        // aborted one used to switch it off on its way out, so the skeleton
        // disappeared while its replacement was still in flight and the table
        // sat blank for a few hundred milliseconds before the rows landed.
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    loadCompanies();

    // Cleanup: cancelar petición si el componente se desmonta
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [getCompanies, refreshKey, currentPage, itemsPerPage, searchTerm, filters, sortConfig]);

  const handleSearch = useCallback((term, selectedField = 'name') => {
    setSearchTerm(term);
    setSelectedField(selectedField);
    setCurrentPage(1); // Reset a primera página cuando cambia la búsqueda
    
    // Nota: handleSearch ahora solo se usa para compatibilidad
    // La lógica real de agregar filtros se maneja en handleFilter
    // que es llamado desde SearchFilter cuando se agrega un chip
    if (term && term.trim()) {
      const trimmedTerm = term.trim();
      // Agregar o actualizar el filtro del campo seleccionado sin limpiar otros
      setFilters(prev => ({
        ...prev,
        [selectedField]: trimmedTerm
      }));
    }
  }, []);

  const handleFilter = useCallback((newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1); // Reset a primera página cuando cambian filtros
  }, []);

  const handleStatusChange = useCallback((status) => {
    setCurrentPage(1);
    if (status && status !== 'all') {
      setFilters(prev => ({ ...prev, status }));
    } else {
      setFilters(prev => {
        const newFilters = { ...prev };
        delete newFilters.status;
        return newFilters;
      });
    }
  }, []);

  const handleSort = useCallback((key, direction) => {
    setSortConfig({ key, direction });
  }, []);

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  const handleItemsPerPageChange = useCallback((items) => {
    setItemsPerPage(items);
    setCurrentPage(1);
  }, []);

  const handleCreateCompany = useCallback(async (companyData) => {
    try {
      const result = await createCompany(companyData);
      
      if (result && result.code === 200) {
        // Trigger refresh by updating refreshKey
        setRefreshKey(prev => prev + 1);
        return { success: true, data: result };
      } else {
        return { success: false, error: result?.message || 'Error creating company' };
      }
    } catch (error) {
      return { success: false, error: error.message || 'Error creating company' };
    }
  }, [createCompany]);

  // Calcular total de páginas basado en el total del servidor
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  return {
    companies: filteredCompanies,
    loading,
    error,
    pagination: {
      currentPage,
      totalPages,
      totalItems: totalItems,
      itemsPerPage,
    },
    filters,
    searchTerm,
    selectedField,
    handleSearch,
    handleFilter,
    handleStatusChange,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleCreateCompany,
  };
};

export default useCompanies;
